import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck, CheckCircle2, XCircle, AlertTriangle, ArrowLeft, PenLine } from 'lucide-react';
import { aggregateDrawReadiness } from '@/lib/drawReadiness';

function ScoreRing({ score, stroke }) {
  const r = 30;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const off = c * (1 - pct / 100);
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" className="shrink-0">
      <circle cx="38" cy="38" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
      <circle
        cx="38" cy="38" r={r} fill="none" stroke={stroke} strokeWidth="7"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        transform="rotate(-90 38 38)"
      />
      <text x="38" y="44" textAnchor="middle" fontSize="19" fontWeight="700" fill="#0f172a">{score}</text>
    </svg>
  );
}

// Draw-scoped readiness gate — shown after item selection, before preview/signature.
// Soft reminder: below 100 the primary button requires a "submit anyway" confirm.
export default function DrawScoreGate({ isOpen, onClose, onContinue, phases = [], contractors = [], contingencyRemaining = 0 }) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { if (!isOpen) setConfirming(false); }, [isOpen]);

  const contractorsById = useMemo(() => {
    const m = {};
    (contractors || []).forEach((c) => { if (c && c.id) m[c.id] = c; });
    return m;
  }, [contractors]);

  const agg = useMemo(
    () => aggregateDrawReadiness(phases, contractorsById, contingencyRemaining),
    [phases, contractorsById, contingencyRemaining]
  );
  const { score, ready, doneSteps, totalSteps, itemsNeedingAttention, perItem } = agg;

  const tone = ready ? 'green' : (score >= 50 ? 'amber' : 'red');
  const toneText = { green: 'text-emerald-600', amber: 'text-amber-600', red: 'text-red-600' }[tone];
  const toneStroke = { green: '#059669', amber: '#d97706', red: '#dc2626' }[tone];

  const handlePrimary = () => {
    if (ready) { onContinue(); return; }
    if (!confirming) { setConfirming(true); return; }
    onContinue();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[640px] w-[95vw] max-h-[90vh] flex flex-col" data-testid="draw-score-gate">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="w-5 h-5 text-blue-600" /> Draw Score for this request
          </DialogTitle>
          <DialogDescription>
            A quick readiness check on the {phases.length} item{phases.length === 1 ? '' : 's'} in this draw. Finish these for a clean, bank-ready submission.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <ScoreRing score={score} stroke={toneStroke} />
          <div className="min-w-0">
            <div className={`text-sm font-semibold ${toneText}`} data-testid="draw-score-status">
              {ready ? 'Ready to submit' : `${itemsNeedingAttention} item${itemsNeedingAttention === 1 ? '' : 's'} need${itemsNeedingAttention === 1 ? 's' : ''} attention`}
            </div>
            <div className="text-xs text-slate-500 mt-0.5" data-testid="draw-score-checks">
              {doneSteps} of {totalSteps} checks complete across this draw
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1 mt-3">
          {perItem.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6">No line items in this draw to check.</p>
          )}
          {perItem.map((it) => (
            <div
              key={it.id}
              data-testid={`draw-gate-item-${it.id}`}
              className={`rounded-lg border p-3 ${it.ready ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-800 text-sm truncate">{it.name}</span>
                <span className={`text-xs font-bold ${it.ready ? 'text-emerald-600' : 'text-slate-500'}`}>{it.score}/100</span>
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {it.steps.map((s) => (
                  <div key={s.key} className="flex items-center gap-2 text-xs">
                    {s.done
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    <span className={s.done ? 'text-slate-500' : 'text-slate-800 font-medium'}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex-shrink-0 pt-3 border-t mt-2">
          {confirming ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="draw-gate-confirm-strip">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700">
                  {itemsNeedingAttention} item{itemsNeedingAttention === 1 ? '' : 's'} still {itemsNeedingAttention === 1 ? 'is' : 'are'} missing documents. Banks may delay or reject a draw with gaps. Submit anyway?
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setConfirming(false)} data-testid="draw-gate-cancel-confirm">Not yet</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={onContinue} data-testid="draw-gate-confirm-anyway">Yes, submit anyway</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose} data-testid="draw-gate-back">
                <ArrowLeft className="w-4 h-4 mr-2" />Go back &amp; fix items
              </Button>
              <Button
                className={`flex-1 ${ready ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                onClick={handlePrimary}
                data-testid="draw-gate-continue"
              >
                <PenLine className="w-4 h-4 mr-2" />{ready ? 'Continue to sign & submit' : 'Continue anyway'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
